import { Injectable, computed, signal } from '@angular/core';

import { NotificationInput, NotificationItem } from './notification.models';

const DEFAULT_AUTO_CLOSE_BY_KIND: Record<NotificationItem['kind'], number | undefined> = {
  success: 3000,
  info: 5000,
  warning: 7000,
  error: undefined,
};

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly maxQueueSize = 30;
  private readonly queue = signal<NotificationItem[]>([]);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly notifications = computed(() => this.queue());

  success(message: string, options: Omit<NotificationInput, 'kind' | 'message'> = {}): string {
    return this.push({ ...options, kind: 'success', message });
  }

  info(message: string, options: Omit<NotificationInput, 'kind' | 'message'> = {}): string {
    return this.push({ ...options, kind: 'info', message });
  }

  warning(message: string, options: Omit<NotificationInput, 'kind' | 'message'> = {}): string {
    return this.push({ ...options, kind: 'warning', message });
  }

  error(message: string, options: Omit<NotificationInput, 'kind' | 'message'> = {}): string {
    return this.push({ ...options, kind: 'error', message });
  }

  push(input: NotificationInput): string {
    const message = input.message.trim();
    if (!message) {
      return '';
    }

    const id = this.generateId();
    const item: NotificationItem = {
      id,
      kind: input.kind,
      message,
      title: input.title?.trim() || undefined,
      actions: (input.actions ?? []).map((action) => ({
        id: action.id,
        label: action.label,
        closeOnClick: action.closeOnClick !== false,
        handler: action.handler,
      })),
      autoCloseMs: input.autoCloseMs ?? DEFAULT_AUTO_CLOSE_BY_KIND[input.kind],
      dedupeKey: input.dedupeKey?.trim() || undefined,
      source: input.source?.trim() || undefined,
      createdAt: Date.now(),
    };

    if (item.dedupeKey) {
      const duplicated = this.queue().find((existing) => existing.dedupeKey === item.dedupeKey);
      if (duplicated) {
        this.dismiss(duplicated.id);
      }
    }

    const next = [...this.queue(), item];
    while (next.length > this.maxQueueSize) {
      const dropped = next.shift();
      if (dropped) {
        this.clearTimer(dropped.id);
      }
    }
    this.queue.set(next);
    this.armAutoClose(item);
    return item.id;
  }

  dismiss(id: string): void {
    const before = this.queue();
    if (!before.some((item) => item.id === id)) {
      return;
    }
    this.queue.set(before.filter((item) => item.id !== id));
    this.clearTimer(id);
  }

  clear(source?: string): void {
    if (!source) {
      for (const item of this.queue()) {
        this.clearTimer(item.id);
      }
      this.queue.set([]);
      return;
    }
    const sourceKey = source.trim();
    const retained: NotificationItem[] = [];
    for (const item of this.queue()) {
      if (item.source === sourceKey) {
        this.clearTimer(item.id);
      } else {
        retained.push(item);
      }
    }
    this.queue.set(retained);
  }

  runAction(notificationId: string, actionId: string): void {
    const item = this.queue().find((notification) => notification.id === notificationId);
    if (!item) {
      return;
    }
    const action = item.actions.find((candidate) => candidate.id === actionId);
    if (!action) {
      return;
    }
    if (action.closeOnClick) {
      this.dismiss(notificationId);
    }
    try {
      action.handler?.();
    } catch (error) {
      console.error('[NOTIFICATIONS] action handler failed', error);
    }
  }

  private armAutoClose(item: NotificationItem): void {
    if (!item.autoCloseMs || item.autoCloseMs <= 0) {
      return;
    }
    this.clearTimer(item.id);
    const handle = setTimeout(() => {
      this.dismiss(item.id);
    }, item.autoCloseMs);
    this.timers.set(item.id, handle);
  }

  private clearTimer(id: string): void {
    const handle = this.timers.get(id);
    if (!handle) {
      return;
    }
    clearTimeout(handle);
    this.timers.delete(id);
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `n_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
  }
}

